/**
 * Contractors API Service
 *
 * Provides typed API client for contractor management:
 * - Contractor profiles (billing, banking, skills)
 * - Contracts (lifecycle, amendments, PDFs)
 * - Projects (tracking, assignment)
 * - Time entries (ETT/STT, review workflow)
 * - Invoices (generation, line items, status)
 * - Bonuses and reimbursements
 * - Availability and schedules
 */

import apiClient from './api';
import type {
  ContractorProfile,
  Contract,
  Project,
  ContractAmendment,
  ContractorInvoice,
  InvoiceLineItem,
  TimeEntry,
  Bonus,
  Reimbursement,
  AvailabilityBlock,
  ContractorSchedule,
  ScheduleBlock,
  TimeSummary,
  CreateContractorRequest,
  UpdateContractorRequest,
  CreateContractRequest,
  UpdateContractRequest,
  CreateProjectRequest,
  UpdateProjectRequest,
  GenerateInvoiceRequest,
  InvoiceStatusUpdate,
  CreateTimeEntryRequest,
  TimeEntryReviewRequest,
  CreateBonusRequest,
  CreateReimbursementRequest,
  ReimbursementReviewRequest,
  CreateAvailabilityRequest,
  CreateScheduleRequest,
  CreateScheduleBlockRequest,
  ScheduleReviewRequest,
} from '../types/contractors';

const BASE = '/contractors';

// ==================== Contractor Profiles ====================

export const contractorsApi = {
  /** List all contractor profiles */
  list: async (params?: {
    search?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ContractorProfile[]> => {
    const { data } = await apiClient.get(BASE, { params });
    return data;
  },

  /** Get a single contractor profile by user ID */
  getProfile: async (userId: number): Promise<ContractorProfile> => {
    const { data } = await apiClient.get(`${BASE}/${userId}`);
    return data;
  },

  /** Create a new contractor profile */
  createProfile: async (req: CreateContractorRequest): Promise<ContractorProfile> => {
    const { data } = await apiClient.post(BASE, req);
    return data;
  },

  /** Update an existing contractor profile */
  updateProfile: async (userId: number, req: UpdateContractorRequest): Promise<ContractorProfile> => {
    const { data } = await apiClient.put(`${BASE}/${userId}`, req);
    return data;
  },

  /** Delete a contractor profile */
  deleteProfile: async (userId: number): Promise<void> => {
    await apiClient.delete(`${BASE}/${userId}`);
  },
};

// ==================== Contracts ====================

export const contractsApi = {
  /** List contracts with optional filters */
  list: async (params?: {
    contractor_user_id?: number;
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<Contract[]> => {
    const { data } = await apiClient.get(`${BASE}/contracts`, { params });
    return data;
  },

  /** Get a single contract */
  get: async (contractId: string): Promise<Contract> => {
    const { data } = await apiClient.get(`${BASE}/contracts/${contractId}`);
    return data;
  },

  /** Create a new contract */
  create: async (req: CreateContractRequest): Promise<Contract> => {
    const { data } = await apiClient.post(`${BASE}/contracts`, req);
    return data;
  },

  /** Update an existing contract */
  update: async (contractId: string, req: UpdateContractRequest): Promise<Contract> => {
    const { data } = await apiClient.put(`${BASE}/contracts/${contractId}`, req);
    return data;
  },

  /** Delete a contract */
  delete: async (contractId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/contracts/${contractId}`);
  },

  /** Sign a contract */
  sign: async (contractId: string): Promise<Contract> => {
    const { data } = await apiClient.post(`${BASE}/contracts/${contractId}/sign`);
    return data;
  },

  /** Terminate a contract */
  terminate: async (contractId: string, reason: string): Promise<Contract> => {
    const { data } = await apiClient.post(`${BASE}/contracts/${contractId}/terminate`, { reason });
    return data;
  },

  /** Renew a contract */
  renew: async (contractId: string, newEndDate?: string): Promise<Contract> => {
    const { data } = await apiClient.post(`${BASE}/contracts/${contractId}/renew`, {
      end_date: newEndDate,
    });
    return data;
  },

  /** Generate contract PDF */
  generatePdf: async (contractId: string): Promise<{ pdf_s3_key: string; disk_file_id: string }> => {
    const { data } = await apiClient.post(`${BASE}/contracts/${contractId}/generate-pdf`);
    return data;
  },

  /** Download contract PDF */
  downloadPdf: async (contractId: string): Promise<{ url: string; expires_in: number }> => {
    const { data } = await apiClient.get(`${BASE}/contracts/${contractId}/pdf`);
    return data;
  },
};

// ==================== Projects ====================

export const projectsApi = {
  /** List projects with optional filters */
  list: async (params?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<Project[]> => {
    const { data } = await apiClient.get(`${BASE}/projects`, { params });
    return data;
  },

  /** Get a single project */
  get: async (projectId: string): Promise<Project> => {
    const { data } = await apiClient.get(`${BASE}/projects/${projectId}`);
    return data;
  },

  /** Create a new project */
  create: async (req: CreateProjectRequest): Promise<Project> => {
    const { data } = await apiClient.post(`${BASE}/projects`, req);
    return data;
  },

  /** Update an existing project */
  update: async (projectId: string, req: UpdateProjectRequest): Promise<Project> => {
    const { data } = await apiClient.put(`${BASE}/projects/${projectId}`, req);
    return data;
  },

  /** Delete a project */
  delete: async (projectId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/projects/${projectId}`);
  },
};

// ==================== Contract Amendments ====================

export const amendmentsApi = {
  /** List amendments for a contract */
  list: async (contractId: string): Promise<ContractAmendment[]> => {
    const { data } = await apiClient.get(`${BASE}/contracts/${contractId}/amendments`);
    return data;
  },

  /** Get a single amendment */
  get: async (contractId: string, amendmentId: string): Promise<ContractAmendment> => {
    const { data } = await apiClient.get(
      `${BASE}/contracts/${contractId}/amendments/${amendmentId}`
    );
    return data;
  },

  /** Create a new amendment */
  create: async (
    contractId: string,
    req: {
      amendment_type: string;
      description: string;
      old_values?: Record<string, unknown>;
      new_values?: Record<string, unknown>;
      effective_date: string;
    }
  ): Promise<ContractAmendment> => {
    const { data } = await apiClient.post(
      `${BASE}/contracts/${contractId}/amendments`,
      req
    );
    return data;
  },
};

// ==================== Invoices ====================

export const contractorInvoicesApi = {
  /** List invoices with optional filters */
  list: async (params?: {
    contractor_user_id?: number;
    contract_id?: string;
    status?: string;
    billing_month?: string;
    limit?: number;
    offset?: number;
  }): Promise<ContractorInvoice[]> => {
    const { data } = await apiClient.get(`${BASE}/invoices`, { params });
    return data;
  },

  /** Get a single invoice with line items */
  get: async (invoiceId: string): Promise<ContractorInvoice> => {
    const { data } = await apiClient.get(`${BASE}/invoices/${invoiceId}`);
    return data;
  },

  /** Generate a new invoice for a billing period */
  generate: async (req: GenerateInvoiceRequest): Promise<ContractorInvoice> => {
    const { data } = await apiClient.post(`${BASE}/invoices/generate`, req);
    return data;
  },

  /** Update invoice status (e.g., mark as paid) */
  updateStatus: async (invoiceId: string, req: InvoiceStatusUpdate): Promise<ContractorInvoice> => {
    const { data } = await apiClient.patch(`${BASE}/invoices/${invoiceId}/status`, req);
    return data;
  },

  /** Delete a draft invoice */
  delete: async (invoiceId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/invoices/${invoiceId}`);
  },

  /** Generate invoice PDF */
  generatePdf: async (invoiceId: string): Promise<{ pdf_s3_key: string; disk_file_id: string }> => {
    const { data } = await apiClient.post(`${BASE}/invoices/${invoiceId}/generate-pdf`);
    return data;
  },

  /** Download invoice PDF */
  downloadPdf: async (invoiceId: string): Promise<{ url: string; expires_in: number }> => {
    const { data } = await apiClient.get(`${BASE}/invoices/${invoiceId}/pdf`);
    return data;
  },

  /** Get invoice line items */
  getLineItems: async (invoiceId: string): Promise<InvoiceLineItem[]> => {
    const { data } = await apiClient.get(`${BASE}/invoices/${invoiceId}/lines`);
    return data;
  },

  /** Preview invoice before generating (dry run) */
  preview: async (req: GenerateInvoiceRequest): Promise<TimeSummary> => {
    const { data } = await apiClient.post(`${BASE}/invoices/preview`, req);
    return data;
  },
};

// ==================== Time Entries ====================

export const timeEntriesApi = {
  /** List time entries with optional filters */
  list: async (params?: {
    contractor_user_id?: number;
    contract_id?: string;
    project_id?: string;
    status?: string;
    entry_type?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<TimeEntry[]> => {
    const { data } = await apiClient.get(`${BASE}/time-entries`, { params });
    return data;
  },

  /** Get a single time entry */
  get: async (entryId: string): Promise<TimeEntry> => {
    const { data } = await apiClient.get(`${BASE}/time-entries/${entryId}`);
    return data;
  },

  /** Create a new time entry */
  create: async (req: CreateTimeEntryRequest): Promise<TimeEntry> => {
    const { data } = await apiClient.post(`${BASE}/time-entries`, req);
    return data;
  },

  /** Update an existing time entry */
  update: async (
    entryId: string,
    req: Partial<CreateTimeEntryRequest>
  ): Promise<TimeEntry> => {
    const { data } = await apiClient.put(`${BASE}/time-entries/${entryId}`, req);
    return data;
  },

  /** Delete a time entry */
  delete: async (entryId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/time-entries/${entryId}`);
  },

  /** Submit time entries for review */
  submit: async (entryIds: string[]): Promise<TimeEntry[]> => {
    const { data } = await apiClient.post(`${BASE}/time-entries/submit`, {
      entry_ids: entryIds,
    });
    return data;
  },

  /** Review (approve/reject) time entries */
  review: async (req: TimeEntryReviewRequest): Promise<TimeEntry[]> => {
    const { data } = await apiClient.post(`${BASE}/time-entries/review`, req);
    return data;
  },

  /** Get time summary for a billing period */
  getSummary: async (params: {
    contractor_user_id: number;
    contract_id: string;
    billing_month: string;
  }): Promise<TimeSummary> => {
    const { data } = await apiClient.get(`${BASE}/time-entries/summary`, { params });
    return data;
  },
};

// ==================== Bonuses ====================

export const bonusesApi = {
  /** List bonuses with optional filters */
  list: async (params?: {
    contractor_user_id?: number;
    contract_id?: string;
    billing_month?: string;
    limit?: number;
    offset?: number;
  }): Promise<Bonus[]> => {
    const { data } = await apiClient.get(`${BASE}/bonuses`, { params });
    return data;
  },

  /** Get a single bonus */
  get: async (bonusId: string): Promise<Bonus> => {
    const { data } = await apiClient.get(`${BASE}/bonuses/${bonusId}`);
    return data;
  },

  /** Create a new bonus */
  create: async (req: CreateBonusRequest): Promise<Bonus> => {
    const { data } = await apiClient.post(`${BASE}/bonuses`, req);
    return data;
  },

  /** Delete a bonus */
  delete: async (bonusId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/bonuses/${bonusId}`);
  },
};

// ==================== Reimbursements ====================

export const reimbursementsApi = {
  /** List reimbursements with optional filters */
  list: async (params?: {
    contractor_user_id?: number;
    contract_id?: string;
    status?: string;
    billing_month?: string;
    limit?: number;
    offset?: number;
  }): Promise<Reimbursement[]> => {
    const { data } = await apiClient.get(`${BASE}/reimbursements`, { params });
    return data;
  },

  /** Get a single reimbursement */
  get: async (reimbursementId: string): Promise<Reimbursement> => {
    const { data } = await apiClient.get(`${BASE}/reimbursements/${reimbursementId}`);
    return data;
  },

  /** Create a new reimbursement */
  create: async (req: CreateReimbursementRequest): Promise<Reimbursement> => {
    const { data } = await apiClient.post(`${BASE}/reimbursements`, req);
    return data;
  },

  /** Review (approve/reject) a reimbursement */
  review: async (
    reimbursementId: string,
    req: ReimbursementReviewRequest
  ): Promise<Reimbursement> => {
    const { data } = await apiClient.post(
      `${BASE}/reimbursements/${reimbursementId}/review`,
      req
    );
    return data;
  },

  /** Delete a reimbursement */
  delete: async (reimbursementId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/reimbursements/${reimbursementId}`);
  },
};

// ==================== Availability ====================

export const availabilityApi = {
  /** List availability blocks for a contractor */
  list: async (params?: {
    contractor_user_id?: number;
    start_date?: string;
    end_date?: string;
    block_type?: string;
  }): Promise<AvailabilityBlock[]> => {
    const { data } = await apiClient.get(`${BASE}/availability`, { params });
    return data;
  },

  /** Get a single availability block */
  get: async (blockId: string): Promise<AvailabilityBlock> => {
    const { data } = await apiClient.get(`${BASE}/availability/${blockId}`);
    return data;
  },

  /** Create a new availability block */
  create: async (req: CreateAvailabilityRequest): Promise<AvailabilityBlock> => {
    const { data } = await apiClient.post(`${BASE}/availability`, req);
    return data;
  },

  /** Update an availability block */
  update: async (
    blockId: string,
    req: Partial<CreateAvailabilityRequest>
  ): Promise<AvailabilityBlock> => {
    const { data } = await apiClient.put(`${BASE}/availability/${blockId}`, req);
    return data;
  },

  /** Delete an availability block */
  delete: async (blockId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/availability/${blockId}`);
  },
};

// ==================== Schedules ====================

export const schedulesApi = {
  /** List schedules with optional filters */
  list: async (params?: {
    contractor_user_id?: number;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ContractorSchedule[]> => {
    const { data } = await apiClient.get(`${BASE}/schedules`, { params });
    return data;
  },

  /** Get a single schedule with blocks */
  get: async (scheduleId: string): Promise<ContractorSchedule> => {
    const { data } = await apiClient.get(`${BASE}/schedules/${scheduleId}`);
    return data;
  },

  /** Create a new schedule */
  create: async (req: CreateScheduleRequest): Promise<ContractorSchedule> => {
    const { data } = await apiClient.post(`${BASE}/schedules`, req);
    return data;
  },

  /** Update a schedule */
  update: async (
    scheduleId: string,
    req: Partial<CreateScheduleRequest>
  ): Promise<ContractorSchedule> => {
    const { data } = await apiClient.put(`${BASE}/schedules/${scheduleId}`, req);
    return data;
  },

  /** Delete a schedule */
  delete: async (scheduleId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/schedules/${scheduleId}`);
  },

  /** Submit schedule for approval */
  submit: async (scheduleId: string): Promise<ContractorSchedule> => {
    const { data } = await apiClient.post(`${BASE}/schedules/${scheduleId}/submit`);
    return data;
  },

  /** Review (approve/reject) a schedule */
  review: async (
    scheduleId: string,
    req: ScheduleReviewRequest
  ): Promise<ContractorSchedule> => {
    const { data } = await apiClient.post(`${BASE}/schedules/${scheduleId}/review`, req);
    return data;
  },
};

// ==================== Schedule Blocks ====================

export const scheduleBlocksApi = {
  /** List blocks for a schedule */
  list: async (scheduleId: string): Promise<ScheduleBlock[]> => {
    const { data } = await apiClient.get(`${BASE}/schedules/${scheduleId}/blocks`);
    return data;
  },

  /** Add a block to a schedule */
  create: async (
    scheduleId: string,
    req: CreateScheduleBlockRequest
  ): Promise<ScheduleBlock> => {
    const { data } = await apiClient.post(
      `${BASE}/schedules/${scheduleId}/blocks`,
      req
    );
    return data;
  },

  /** Update a schedule block */
  update: async (
    scheduleId: string,
    blockId: string,
    req: Partial<CreateScheduleBlockRequest>
  ): Promise<ScheduleBlock> => {
    const { data } = await apiClient.put(
      `${BASE}/schedules/${scheduleId}/blocks/${blockId}`,
      req
    );
    return data;
  },

  /** Delete a schedule block */
  delete: async (scheduleId: string, blockId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/schedules/${scheduleId}/blocks/${blockId}`);
  },
};

// ==================== Default Export ====================

const contractorsModule = {
  profiles: contractorsApi,
  contracts: contractsApi,
  projects: projectsApi,
  amendments: amendmentsApi,
  invoices: contractorInvoicesApi,
  timeEntries: timeEntriesApi,
  bonuses: bonusesApi,
  reimbursements: reimbursementsApi,
  availability: availabilityApi,
  schedules: schedulesApi,
  scheduleBlocks: scheduleBlocksApi,
};

export default contractorsModule;
